import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import http from '../../shared/http'
import i18nInstance from '../../shared/i18n'
import { TApi, TRoles, TRoutes } from '../../shared/const'

import Btn from '../../UI/Btn'
import Loading from '../../UI/Loading'

import styles from './style.module.scss'

/**
 * Bestaetigung der Firmen-E-Mail-Adresse: das Ziel des Knopfes aus der Mail.
 *
 * Bis 08.08.2026 endete der Klick im Nichts. Zwei Gruende, beide behoben:
 * Die Seite lag hinter der Anmeldung (wer die Mail im Firmenpostfach auf dem
 * Handy oeffnete, landete auf der Anmeldeseite), und selbst angemeldet schob
 * `App.js` sofort auf das Dashboard weiter — die Meldung war nie zu lesen,
 * obwohl die Adresse bestaetigt war.
 *
 * Jetzt: eine oeffentliche Seite, die sagt, was passiert ist, und von dort
 * weiterfuehrt — angemeldet ins Unternehmensprofil (dort steht die Adresse mit
 * dem Vermerk „Verifiziert"), sonst zur Anmeldung.
 */

// So lange bleibt die Bestaetigung stehen, bevor es von selbst weitergeht.
// Kurz genug, um nicht zu warten; lang genug, um den Satz zu lesen.
const FORWARD_DELAY = 5000

export default function CompanyEmailVerifyPage() {
	const { t } = useTranslation()
	const history = useHistory()

	const isAuth = useSelector(state => state.user.isAuth)
	const role = useSelector(state => state.user.role)

	const [status, setStatus] = useState('loading')
	const [alreadyVerified, setAlreadyVerified] = useState(false)
	const [errorText, setErrorText] = useState('')

	// Angemeldet geht es dorthin, wo die bestaetigte Adresse steht. Das
	// Unternehmensprofil gibt es nur fuer Schueler:innen; Lehrkraefte und
	// Verwaltung landen auf ihrer Startseite.
	const target = role === TRoles.STUDENT ? TRoutes.COMPANY : TRoutes.DASHBOARD

	/*
	 * Ohne Anmeldung gibt es keine Sprachwahl in der Leiste, und die
	 * Browsererkennung faellt auf Englisch zurueck. Wer angemeldet ist, behaelt
	 * seine eigene Einstellung.
	 */
	useEffect(() => {
		if (!isAuth && i18nInstance.language !== 'ger') i18nInstance.changeLanguage('ger')
	}, [isAuth])

	useEffect(() => {
		const token = new URLSearchParams(window.location.search).get('token')

		if (!token) {
			setStatus('error')
			setErrorText('The confirmation link is incomplete. Please open it again from the email.')
			return
		}

		http.get(TApi.PUBLIC_COMPANY_VERIFY_EMAIL(token), { disableToast: true })
			.then(({ data }) => {
				setAlreadyVerified(!!data.alreadyVerified)
				setStatus('success')
			})
			.catch(error => {
				setStatus('error')
				/*
				 * Eigene Saetze statt der Serverantwort: Die Seite steht auch
				 * ohne Anmeldung offen und muss zweisprachig sein — das Backend
				 * antwortet nur auf Deutsch.
				 */
				const byStatus = {
					400: 'This confirmation link has expired or is invalid. A new confirmation email can be requested in the company profile.',
					404: 'There is no Junior Company for this email address. Has the address been changed in the meantime?'
				}
				setErrorText(
					byStatus[error.response?.status] ||
						'The email address could not be confirmed. Please try again later.'
				)
			})
	}, [])

	// Weiterleiten nur fuer Angemeldete: Sonst waere das Ziel die Anmeldemaske,
	// und die kaeme ungefragt statt der eben gelesenen Bestaetigung.
	const forwarded = useRef(false)
	useEffect(() => {
		if (status !== 'success' || !isAuth || forwarded.current) return

		const timer = setTimeout(() => {
			forwarded.current = true
			toast.success(t('The company email address has been confirmed.'))
			history.push(target)
		}, FORWARD_DELAY)

		return () => clearTimeout(timer)
		// `t` gehoert bewusst nicht in die Liste: Die Funktion ist bei jedem
		// Render neu, der Zeitgeber liefe sonst immer wieder von vorn los.
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [status, isAuth, history, target])

	const goOn = () => {
		forwarded.current = true
		history.push(isAuth ? target : TRoutes.SIGN_IN)
	}

	return (
		<div className={styles.root}>
			<div className={styles.card}>
				{status === 'loading' && (
					<>
						<h1 className={styles.title}>{t('Confirming email address …')}</h1>
						<Loading />
					</>
				)}

				{status === 'success' && (
					<>
						<div className={styles.mark} aria-hidden="true">
							<svg viewBox="0 0 24 24" width="28" height="28" fill="none">
								<path
									d="M4 12.5l5.2 5.2L20 7"
									stroke="currentColor"
									strokeWidth="2.4"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</div>
						<h1 className={styles.title}>{t('Email address confirmed')}</h1>
						<p className={styles.text}>
							{alreadyVerified
								? t('This email address was already confirmed. Nothing else to do.')
								: t(
										'The company email address is now confirmed. This is one of the requirements for submitting your company for approval.'
								  )}
						</p>
						{isAuth && <p className={styles.hint}>{t('You will be forwarded in a moment …')}</p>}
						<Btn onClick={goOn}>
							{isAuth
								? t(role === TRoles.STUDENT ? 'To the company profile' : 'To the dashboard')
								: t('Login')}
						</Btn>
					</>
				)}

				{status === 'error' && (
					<>
						<h1 className={styles.title}>{t('Email address could not be confirmed')}</h1>
						<p className={styles.text}>{t(errorText)}</p>
						<Btn onClick={goOn}>
							{isAuth
								? t(role === TRoles.STUDENT ? 'To the company profile' : 'To the dashboard')
								: t('Login')}
						</Btn>
					</>
				)}
			</div>
		</div>
	)
}
